/**
 * queryClient.js — TanStack Query client configuration
 *
 * Central query client with defaults tuned for RFOS:
 * - 30s stale time: data is considered fresh for 30 seconds before background refetch
 * - 5 min gcTime: unused cache entries are garbage collected after 5 minutes
 * - Retry with backoff: failed queries retry 2x with exponential backoff
 * - Refetch on window focus: keeps data fresh when user tabs back
 *
 * All data fetching in the app goes through TanStack Query via custom hooks.
 * WebSocket events trigger cache invalidation (see useRealtime).
 */

import { QueryClient } from '@tanstack/react-query';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000,           // 30 seconds — data is "fresh" this long
      gcTime: 5 * 60 * 1000,          // 5 minutes — unused cache kept this long
      retry: 2,                        // Retry failed fetches twice
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 10000), // Exponential backoff
      refetchOnWindowFocus: true,      // Refresh stale data when user returns
      refetchOnReconnect: true,        // Refresh when network reconnects
    },
    mutations: {
      // Retry transient failures only. A 4xx is the server declining on purpose —
      // retrying it cannot succeed, and for a deliberate refusal like the 409
      // job_identity_locked guard it just writes the block to the audit log twice.
      retry: (failureCount, error) => {
        const status = error?.status;
        if (status >= 400 && status < 500) return false;
        return failureCount < 1;
      },
      retryDelay: 1000,
    },
  },
});

/**
 * Query key factory — consistent key patterns for cache management.
 *
 * Usage:
 *   queryClient.invalidateQueries({ queryKey: queryKeys.jobs.all })
 *   useQuery({ queryKey: queryKeys.jobs.detail(id), ... })
 */
export const queryKeys = {
  jobs:           { all: ['jobs'],           detail: (id) => ['jobs', id] },
  contacts:       { all: ['contacts'],       detail: (id) => ['contacts', id] },
  invoices:       { all: ['invoices'],       detail: (id) => ['invoices', id], byJob: (jobId) => ['invoices', 'job', jobId] },
  timeEntries:    { all: ['time_entries'],   byJob: (jobId) => ['time_entries', 'job', jobId] },
  expenses:       { all: ['expenses'],       byJob: (jobId) => ['expenses', 'job', jobId] },
  staticExpenses: { all: ['static_expenses'] },
  legalDocs:      { all: ['legal_docs'],     detail: (id) => ['legal_docs', id] },
  actionLog:      { all: ['action_log'] },
  payPeriods:     { all: ['pay_periods'],    detail: (id) => ['pay_periods', id] },
  payPayments:    { all: ['pay_period_payments'] },
  crewEarnings:   { all: ['crew_earnings'], detail: (id) => ['crew_earnings', id] },
  settings:       { all: ['settings'] },
  roster:         { all: ['roster'] },
  profiles:       { all: ['profiles'],       detail: (id) => ['profiles', id] },
  quotes:         { all: ['quotes'],         detail: (id) => ['quotes', id] },
  outreach:       { all: ['outreach_actions'], byContact: (cid) => ['outreach_actions', 'contact', cid] },
  calendarEvents: { all: ['calendar_events'] },
  voicenotes:     { all: ['voicenotes'],      detail: (id) => ['voicenotes', id], productionOutline: (slug) => ['voicenotes', 'production-outline', slug] },
  voicePlans:     { all: ['voice_plans'],    detail: (id) => ['voice_plans', id] },
  todos:          {
    all: ['todos'],
    list: (view, assignee) => ['todos', 'list', view, assignee || ''],
    one: (id) => ['todos', 'one', id],
    summary: ['todos', 'summary'],
    suggestions: ['todos', 'suggestions'],
    people: ['todos', 'people'],
    voicenoteItems: (ids) => ['todos', 'vn', ids],
  },
  projects:       {
    all: ['projects'],
    tree: ['projects', 'tree'],
    detail: (id) => ['projects', id],
    full: (id) => ['projects', id, 'full'],
    rollup: (id, scope) => ['projects', id, 'rollup', scope || 'subtree'],
    timeEntries: (id, scope) => ['projects', id, 'time-entries', scope || 'subtree'],
    expenses: (id, scope) => ['projects', id, 'expenses', scope || 'subtree'],
  },
  projectTasks:   { all: ['project_tasks'] },
  projectNotes:   { all: ['project_notes'] },
  projectFiles:   { all: ['project_files'] },
  tessie:         { status: ['tessie', 'status'], config: ['tessie', 'config'], drives: ['tessie', 'drives'], summary: ['tessie', 'summary'] },
  links:          { all: ['links'], detail: (id) => ['links', id], clicks: (id, limit, offset, from, to) => ['links', id, 'clicks', limit, offset, from, to], stats: (id, days) => ['links', id, 'stats', days] },
  wikiArticles:   { all: ['wiki_articles'], detail: (id) => ['wiki_articles', id] },
  media:          { all: ['media'], favorites: ['media', 'favorites'], collections: ['media', 'collections'], collection: (id) => ['media', 'collection', id], shares: ['media', 'shares'] },
  openHouses:     { all: ['open_houses'], detail: (id) => ['open_houses', id] },
  ohPlans:        { all: ['oh_plans'], detail: (id) => ['oh_plans', id] },
  ohPlanStops:    { all: ['oh_plan_stops'], byPlan: (planId) => ['oh_plan_stops', 'plan', planId] },
  carousels:      { all: ['carousels'], detail: (id) => ['carousels', id] },
  trash:          { all: ['trash'] },
  feedback:       { all: ['feedback'] },
};

/**
 * Maps DB table names to their TanStack Query keys.
 * Used by useRealtime to invalidate the right cache on remote changes.
 */
export const tableToQueryKey = {
  jobs: queryKeys.jobs.all,
  contacts: queryKeys.contacts.all,
  invoices: queryKeys.invoices.all,
  time_entries: queryKeys.timeEntries.all,
  expenses: queryKeys.expenses.all,
  static_expenses: queryKeys.staticExpenses.all,
  legal_docs: queryKeys.legalDocs.all,
  action_log: queryKeys.actionLog.all,
  pay_periods: queryKeys.payPeriods.all,
  pay_period_payments: queryKeys.payPayments.all,
  crew_earnings: queryKeys.crewEarnings.all,
  settings: queryKeys.settings.all,
  roster: queryKeys.roster.all,
  quotes: queryKeys.quotes.all,
  outreach_actions: queryKeys.outreach.all,
  calendar_events: queryKeys.calendarEvents.all,
  voicenotes: queryKeys.voicenotes.all,
  todos: queryKeys.todos.all,
  production_outlines: queryKeys.voicenotes.all,
  projects: queryKeys.projects.all,
  project_tasks: queryKeys.projectTasks.all,
  project_notes: queryKeys.projectNotes.all,
  project_files: queryKeys.projectFiles.all,
  job_earnings_ledger: ['job-earnings-ledger'],
  wiki_articles: queryKeys.wikiArticles.all,
  // RotoTube viewer tools — broad ['media'] key refreshes favorites/collections/
  // shares across clients on any change (mutations already refresh the actor).
  media_items: queryKeys.media.all,
  media_favorites: queryKeys.media.all,
  media_collections: queryKeys.media.all,
  media_collection_items: queryKeys.media.all,
  media_shares: queryKeys.media.all,
  // Broad ['links'] prefix also invalidates per-link clicks/stats queries,
  // so new scans (e.g. truck QR) appear live on an open Links page.
  links: queryKeys.links.all,
  link_clicks: queryKeys.links.all,
  // Open House planner — broad prefixes so per-plan stop queries invalidate too
  open_houses: queryKeys.openHouses.all,
  oh_plans: queryKeys.ohPlans.all,
  oh_plan_stops: queryKeys.ohPlanStops.all,
  carousels: queryKeys.carousels.all,
  deleted_records: queryKeys.trash.all,
  feedback: queryKeys.feedback.all,
};
